import type { FrequencyData } from '../../../services/AudioManager';
import { readAnalyserFrequencyData } from '../../../utils/analyserFrequencyBands';

export const GEMINI_TTS_SAMPLE_RATE = 24000;

type DrainCallback = () => void;

export class PCMStreamPlayer {
  private _ctx: AudioContext;
  private _sampleRate: number;
  private _nextStartTime = 0;
  private _activeSources = new Set<AudioBufferSourceNode>();
  private _drainCallbacks = new Set<DrainCallback>();
  private _drainTimer: ReturnType<typeof setTimeout> | null = null;
  private _analyser: AnalyserNode;
  private _frequencyDataArray: Uint8Array;

  constructor(audioContext: AudioContext, sampleRate: number) {
    this._ctx = audioContext;
    this._sampleRate = sampleRate;
    this._analyser = this._ctx.createAnalyser();
    this._analyser.fftSize = 2048;
    this._frequencyDataArray = new Uint8Array(this._analyser.frequencyBinCount);
    this._analyser.connect(this._ctx.destination);
  }

  getFrequencyData(): FrequencyData {
    return readAnalyserFrequencyData(
      this._analyser,
      this._frequencyDataArray,
      this._ctx.sampleRate,
    );
  }

  queue(arrayBuffer: ArrayBuffer): void {
    const int16 = new Int16Array(arrayBuffer);
    if (int16.length === 0) return;
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
    }
    const buffer = this._ctx.createBuffer(1, float32.length, this._sampleRate);
    buffer.copyToChannel(float32, 0);
    const source = this._ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this._analyser);
    const now = this._ctx.currentTime;
    const startAt = Math.max(now, this._nextStartTime);
    source.start(startAt);
    this._nextStartTime = startAt + buffer.duration;
    this._activeSources.add(source);
    source.onended = () => {
      this._activeSources.delete(source);
      this._scheduleDrainCheck();
    };
    this._scheduleDrainCheck();
  }

  onDrain(callback: DrainCallback): () => void {
    this._drainCallbacks.add(callback);
    return () => this._drainCallbacks.delete(callback);
  }

  stop(): void {
    this._activeSources.forEach((source) => {
      try {
        source.stop();
      } catch {
        // already stopped
      }
    });
    this._activeSources.clear();
    this._nextStartTime = 0;
    if (this._drainTimer) {
      clearTimeout(this._drainTimer);
      this._drainTimer = null;
    }
  }

  hasPendingAudio(): boolean {
    return this._activeSources.size > 0 || this._nextStartTime > this._ctx.currentTime + 0.05;
  }

  private _scheduleDrainCheck(): void {
    if (this._drainTimer) {
      clearTimeout(this._drainTimer);
    }
    if (this.hasPendingAudio()) {
      const delayMs = Math.max(0, (this._nextStartTime - this._ctx.currentTime) * 1000 + 50);
      this._drainTimer = setTimeout(() => this._emitDrainIfIdle(), delayMs);
      return;
    }
    this._emitDrainIfIdle();
  }

  private _emitDrainIfIdle(): void {
    this._drainTimer = null;
    if (this.hasPendingAudio()) {
      this._scheduleDrainCheck();
      return;
    }
    this._drainCallbacks.forEach((callback) => {
      callback();
    });
  }
}
